import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { Sparkles } from 'lucide-react';

import { FormattedMessage } from '../../../util/reactIntl';
import { aiConciergeParse } from '../../../util/api';
import { track } from '../../../util/analytics';
import {
  conciergeRequestPayload,
  intentToSearchParams,
  isAiConciergeEnabled,
  markAiConciergeUnavailable,
  mergeLocationIntoKeywords,
  normalizeSearchParams,
  redactPii,
  sliceWholeChars,
} from '../../../util/aiConcierge';
import { LISTING_TYPE_PARAM } from '../../../util/searchQuickFilters';
import { createResourceLocatorString } from '../../../util/routes';
import { omitUiOnlyParams, parse } from '../../../util/urlHelpers';
import GeocoderMapbox from '../../../components/LocationAutocompleteInput/GeocoderMapbox';
import { omitLimitedListingFieldParams } from '../SearchPage.shared';

import css from './SearchAiConcierge.module.css';

// Carried over when the request resolved to nothing at all — the filters the
// visitor set deliberately and that free text rarely means to replace. Notably
// NOT the location or the dates: see carriedFilters below.
const FALLBACK_CARRIED_PARAMS = [LISTING_TYPE_PARAM, 'price', 'sort'];
const LOCATION_PARAMS = ['address', 'bounds', 'origin'];

// Matches MAX_TEXT_LENGTH in server/api/ai-concierge.js.
const MAX_TEXT_LENGTH = 300;

/**
 * Forward-geocode a place the intent could not resolve to a known city.
 * Reuses the Mapbox geocoder of the topbar location search (plain Geocoding API
 * call, needs only the access token). Never throws — a null return
 * means "fold the place into the keyword search instead".
 */
const geocodePlace = async (locationName, locale) => {
  try {
    const geocoder = new GeocoderMapbox();
    const { predictions } = await geocoder.getPlacePredictions(locationName, null, locale);
    if (!predictions || predictions.length === 0) {
      return null;
    }
    const place = await geocoder.getPlaceDetails(predictions[0]);
    return place?.bounds ? { address: place.address || locationName, bounds: place.bounds } : null;
  } catch (e) {
    return null;
  }
};

/**
 * The AI concierge bar on the SearchPage: describe in plain language when and
 * where you need a space, and the filters apply themselves.
 *
 * Free text goes to POST /api/ai-concierge (Claude behind a strict schema,
 * see server/api/ai-concierge.js), the returned intent becomes ordinary URL
 * search params (src/util/aiConcierge.js), and a history.push to the plain
 * SearchPage route lets the existing loadData pipeline run the search — the
 * concierge deliberately owns no search logic of its own. Always pushes to
 * /s, never /s/:listingType: the path param would pin pub_listingType over
 * whatever the concierge resolved (same escape as handleDurationChange).
 *
 * If anything fails — endpoint down, no key, model error — the raw text is
 * submitted as a normal keyword search, so the bar never dead-ends.
 *
 * pushSearch merges the AI's fields over the page's CURRENT filters — an
 * AI-derived field wins on conflict, and anything the request didn't address
 * survives. How much survives depends on how much resolved (see
 * carriedFilters): a request the model could not parse at all keeps only the
 * deliberate, non-locational filters, because carrying the old place and dates
 * under a raw keyword search is a guaranteed dead end. On /s/:listingType the
 * path pin has no query-string representation of its own, so it is seeded into
 * the merge explicitly, ahead of any pub_listingType left in the query.
 *
 * A ref-tracked location snapshot guards two races: if something else
 * navigates (a quick-filter chip, the LandingPage mode toggle) while a
 * request is in flight, a since-resolved response is stale and skips its own
 * navigation instead of overwriting the newer one; and if the component
 * unmounts entirely (toggle switched to the classic form) before the request
 * resolves, neither setState nor history.push fire on the gone instance.
 *
 * First render is static markup only (no window access, no portal), per the
 * SSR/hydration rules in CLAUDE.md.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {Object} props.config marketplace config (categories, listing types)
 * @param {Object} props.intl
 * @param {Object} props.history
 * @param {Array} props.routeConfiguration
 * @param {Object} props.location current router location — read at submit time
 *   for the current filters and again after the await to detect a race
 * @param {Object} [props.params] current path params (e.g. {listingType} on
 *   /s/:listingType) — absent on the LandingPage mount, where there is none
 * @returns {JSX.Element|null}
 */
const SearchAiConcierge = props => {
  const { className, config, intl, history, routeConfiguration, location, params } = props;

  const [text, setText] = useState('');
  // 'idle' | 'thinking' | 'done' | 'fallback'
  const [status, setStatus] = useState('idle');
  const [reply, setReply] = useState(null);

  const isMountedRef = useRef(true);
  const locationRef = useRef(location);
  locationRef.current = location;
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (!isAiConciergeEnabled()) {
    return null;
  }

  const isThinking = status === 'thinking';

  const pushSearch = searchParams => {
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };

  // Reuses the SearchPage's own rule for which listing-field params a category
  // allows, rather than restating it here. Guarded because the concierge also
  // mounts on the LandingPage, where a partially-built config is plausible and
  // dropping every filter would be worse than keeping an odd one.
  const withValidListingFieldParams = merged => {
    const defaultFiltersConfig = config?.search?.defaultFilters;
    const listingCategories = config?.categoryConfiguration?.categories;
    if (!defaultFiltersConfig || !listingCategories) {
      return merged;
    }
    return omitLimitedListingFieldParams(merged, {
      listingFieldsConfig: config?.listing?.listingFields,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes: config?.listing?.listingTypes?.map(t => t.listingType),
      currentPathParams: params || {},
    });
  };

  /**
   * The page's current filters, carried into the new search. What survives
   * depends on how much of the request actually resolved:
   *
   *  - `page`/`mapSearch` never survive a filter change — every other path in
   *    the app drops them too (validUrlQueryParamsFromProps). Keeping `page`
   *    lands a refined search on page 3 of a result set that may now be one
   *    page long.
   *  - `keywords` never survives either. It is either this feature's own
   *    fallback text from an earlier submit — a whole sentence that would now
   *    AND against the new filters and guarantee zero results — or a keyword
   *    the new request supersedes. The new text always speaks for itself.
   *  - On the fallback path nothing resolved, so only the deliberate,
   *    non-locational filters carry. Carrying the old place and dates there
   *    searches Geneva words inside Zürich bounds: a dead end, which is
   *    precisely what this bar promises never to produce.
   *  - `keepLocation: false` also covers a named-but-ungeocodable place: the
   *    words say Lugano while the carried bounds still say Zürich.
   */
  const carriedFilters = ({ resolved, keepLocation }) => {
    // eslint-disable-next-line no-unused-vars
    const { page, mapSearch, keywords, ...rest } = omitUiOnlyParams(
      parse(locationRef.current.search)
    );
    return Object.fromEntries(
      Object.entries(rest).filter(
        ([key]) =>
          (resolved || FALLBACK_CARRIED_PARAMS.includes(key)) &&
          (keepLocation || !LOCATION_PARAMS.includes(key))
      )
    );
  };

  /**
   * Merge order encodes two precedence rules:
   *  - the `/s/:listingType` path pin beats a `pub_listingType` left in the
   *    query string (the "Länge" capsules write one even on a pinned path),
   *    matching the ordering SearchPage.duck.js documents for the same pair;
   *  - the intent beats both, since answering the visitor's sentence is the
   *    whole point.
   *
   * Then the merged result — not the intent alone — is validated: a category
   * the intent just changed can invalidate a listing-field param carried from
   * the URL, and `pickListingFieldFilters` would remove that filter's chip
   * while the duck still forwarded the param to listings/query, leaving an
   * invisible filter the visitor cannot see or clear.
   */
  const mergeParams = (carried, searchParams) =>
    normalizeSearchParams(
      withValidListingFieldParams({
        ...carried,
        ...(params?.listingType ? { [LISTING_TYPE_PARAM]: params.listingType } : {}),
        ...searchParams,
      })
    );

  const handleSubmit = async e => {
    e.preventDefault();
    const trimmed = sliceWholeChars(text.trim(), MAX_TEXT_LENGTH);
    if (!trimmed || isThinking) {
      return;
    }

    // Both halves of the location are snapshotted: a navigation that changes
    // only the path (/s/meeting-room → /s/coworking) leaves `search`
    // untouched, and `params.listingType` — seeded into every merge below —
    // comes from that path.
    const { pathname: pathnameAtSubmit, search: searchAtSubmit } = locationRef.current;
    const isStillOnSubmitLocation = () =>
      locationRef.current.pathname === pathnameAtSubmit &&
      locationRef.current.search === searchAtSubmit;
    setStatus('thinking');
    setReply(null);
    const locale = `${intl.locale || 'de'}`.split('-')[0];

    try {
      const { intent } = await aiConciergeParse(conciergeRequestPayload(trimmed, config, locale));
      let { searchParams, geocodeLocationName } = intentToSearchParams(
        intent,
        config?.listing?.listingTypes
      );

      // A place the intent named but could not resolve: it becomes keywords,
      // and the previous location must NOT carry — otherwise the words say
      // Lugano while the bounds still say Zürich. geocodePlace also returns
      // null when Mapbox simply has not loaded yet (it is deferred on the
      // LandingPage, where the concierge is the default hero), so this path is
      // routine rather than exotic.
      let geocodeFailed = false;
      if (geocodeLocationName) {
        const place = await geocodePlace(geocodeLocationName, locale);
        if (place) {
          searchParams = { ...searchParams, address: place.address, bounds: place.bounds };
        } else {
          geocodeFailed = true;
          searchParams = mergeLocationIntoKeywords(searchParams, redactPii(geocodeLocationName));
        }
      }

      if (!isMountedRef.current) {
        return;
      }

      track.aiConciergeSearch({
        search_term: redactPii(trimmed),
        ai_fallback: 'no',
        // citySlug is enum-constrained by the server schema; locationName is
        // free text the model wrote, so it gets the same scrub as the query.
        ai_city:
          intent?.citySlug || (intent?.locationName && redactPii(intent.locationName)) || undefined,
        ai_category: intent?.categoryId || undefined,
      });

      // Nothing usable came back: search the raw text as keywords, keeping
      // only the filters a bare sentence does not speak to.
      const hasAnyMappedFilter = Object.keys(searchParams).length > 0;
      const finalParams = hasAnyMappedFilter
        ? mergeParams(
            carriedFilters({ resolved: true, keepLocation: !geocodeFailed }),
            searchParams
          )
        : mergeParams(carriedFilters({ resolved: false, keepLocation: false }), {
            keywords: redactPii(trimmed),
          });

      setStatus('done');
      setReply(intent?.reply || null);

      if (isStillOnSubmitLocation()) {
        pushSearch(finalParams);
      }
    } catch (err) {
      if (!isMountedRef.current) {
        return;
      }
      // A 503 means the endpoint is configured off or the daily cap tripped.
      // Remember that: the Topbar hides its own location search wherever this
      // bar renders, and a keyword-only concierge plus no topbar form would
      // leave no way to search by place at all.
      if (err?.error === 'AI_CONCIERGE_DISABLED') {
        markAiConciergeUnavailable();
      }
      // Endpoint unavailable or parse failed: plain keyword search instead.
      track.aiConciergeSearch({ search_term: redactPii(trimmed), ai_fallback: 'yes' });
      setStatus('fallback');
      if (isStillOnSubmitLocation()) {
        pushSearch(
          mergeParams(carriedFilters({ resolved: false, keepLocation: false }), {
            keywords: redactPii(trimmed),
          })
        );
      }
    }
  };

  const placeholder = intl.formatMessage({ id: 'SearchAiConcierge.placeholder' });

  // Idle-and-empty breathes gently (invites input); thinking gets a tight
  // twinkle; a typed-but-not-yet-submitted field stays static petrol — see
  // SearchAiConcierge.module.css for the full state rationale.
  const iconStateClass =
    status === 'idle' && !text.trim() ? css.iconIdleEmpty : isThinking ? css.iconThinking : null;

  return (
    <div className={classNames(css.root, className)}>
      <form
        className={classNames(css.form, {
          [css.formThinking]: isThinking,
          [css.formReply]: status === 'done',
        })}
        onSubmit={handleSubmit}
        role="search"
        aria-label={intl.formatMessage({ id: 'SearchAiConcierge.groupLabel' })}
      >
        <Sparkles
          className={classNames(css.icon, iconStateClass)}
          size={18}
          strokeWidth={2}
          aria-hidden="true"
        />
        <input
          className={css.input}
          type="text"
          value={text}
          maxLength={MAX_TEXT_LENGTH}
          onChange={e => setText(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
        />
        <button type="submit" className={css.submit} disabled={isThinking || !text.trim()}>
          <FormattedMessage id="SearchAiConcierge.submit" />
        </button>
      </form>
      {isThinking ? (
        <p className={css.thinkingStatus} role="status">
          <FormattedMessage id="SearchAiConcierge.thinking" />
        </p>
      ) : null}
      {status === 'done' && reply ? (
        <p className={css.reply} role="status">
          <Sparkles className={css.replyIcon} size={15} strokeWidth={2} aria-hidden="true" />
          <span>{reply}</span>
        </p>
      ) : null}
      {status === 'fallback' ? (
        <p className={classNames(css.status, css.fallback)} role="status">
          <FormattedMessage id="SearchAiConcierge.fallback" />
        </p>
      ) : null}
    </div>
  );
};

export default SearchAiConcierge;
